// Create / edit form for one bank_accounts row (pages/BankAccountsPage).
// Same "plain useState, no react-hook-form" shape as UserForm.jsx /
// ProductForm.jsx — this app's inline-panel create/edit forms all follow
// that sibling pattern rather than the stack note's react-hook-form.
//
// One component for both create and edit: pass `initialValues` (an existing
// row) to edit, omit it to create. The account/routing numbers a proforma
// prints have to be exactly right, so nothing here is auto-filled or
// guessed — every field is typed by the admin.

import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext.jsx';
import { Spinner } from 'shared/components/Feedback/index.js';
import styles from './BankAccountForm.module.css';

function stateFrom(initialValues) {
  return {
    bankName: initialValues?.bank_name || '',
    accountName: initialValues?.account_name || '',
    accountNumber: initialValues?.account_number || '',
    branch: initialValues?.branch || '',
    swift: initialValues?.swift || '',
    sortOrder: initialValues?.sort_order != null ? String(initialValues.sort_order) : '0',
  };
}

export default function BankAccountForm({ initialValues, onSubmit, onCancel, isPending }) {
  const { t } = useLanguage();
  const isEdit = Boolean(initialValues);
  const [values, setValues] = useState(() => stateFrom(initialValues));
  const [clientErrorId, setClientErrorId] = useState(null);

  const set = (field) => (e) => setValues((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setClientErrorId(null);

    if (!values.bankName.trim()) {
      setClientErrorId('bankAccounts.form.bankNameRequired');
      return;
    }
    if (!values.accountName.trim()) {
      setClientErrorId('bankAccounts.form.accountNameRequired');
      return;
    }
    if (!values.accountNumber.trim()) {
      setClientErrorId('bankAccounts.form.accountNumberRequired');
      return;
    }

    const sortOrder = Number(values.sortOrder);
    onSubmit({
      bankName: values.bankName.trim(),
      accountName: values.accountName.trim(),
      accountNumber: values.accountNumber.trim(),
      branch: values.branch.trim() || (isEdit ? null : undefined),
      swift: values.swift.trim() || (isEdit ? null : undefined),
      sortOrder: Number.isFinite(sortOrder) && sortOrder >= 0 ? sortOrder : 0,
    });
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <h2 className={styles.title}>
        {isEdit ? t('bankAccounts.form.editTitle') : t('bankAccounts.form.createTitle')}
      </h2>

      <div className={styles.grid}>
        <label className={styles.field}>
          {t('bankAccounts.form.bankNameLabel')}
          <input
            type="text"
            value={values.bankName}
            onChange={set('bankName')}
            autoFocus
            maxLength={120}
            className={styles.input}
          />
        </label>

        <label className={styles.field}>
          {t('bankAccounts.form.accountNameLabel')}
          <input
            type="text"
            value={values.accountName}
            onChange={set('accountName')}
            maxLength={120}
            className={styles.input}
          />
        </label>

        <label className={styles.field}>
          {t('bankAccounts.form.accountNumberLabel')}
          <input
            type="text"
            value={values.accountNumber}
            onChange={set('accountNumber')}
            maxLength={60}
            className={`${styles.input} tabularNums`}
          />
        </label>

        <label className={styles.field}>
          {t('bankAccounts.form.branchLabel')}
          <input
            type="text"
            value={values.branch}
            onChange={set('branch')}
            maxLength={120}
            className={styles.input}
          />
        </label>

        <label className={styles.field}>
          {t('bankAccounts.form.swiftLabel')}
          <input
            type="text"
            value={values.swift}
            onChange={set('swift')}
            maxLength={20}
            className={`${styles.input} tabularNums`}
          />
        </label>

        <label className={styles.field}>
          {t('bankAccounts.form.sortOrderLabel')}
          <input
            type="number"
            min={0}
            value={values.sortOrder}
            onChange={set('sortOrder')}
            className={`${styles.input} tabularNums`}
          />
          <span className={styles.hint}>{t('bankAccounts.form.sortOrderHint')}</span>
        </label>
      </div>

      {clientErrorId && <p className={styles.error}>{t(clientErrorId)}</p>}

      <div className={styles.actions}>
        <button type="button" className={styles.cancelButton} onClick={onCancel}>
          {t('bankAccounts.form.cancel')}
        </button>
        <button type="submit" className={styles.submitButton} disabled={isPending}>
          {isPending && <Spinner />}{' '}
          {isPending
            ? t('bankAccounts.form.submitting')
            : isEdit
              ? t('bankAccounts.form.submitEdit')
              : t('bankAccounts.form.submitCreate')}
        </button>
      </div>
    </form>
  );
}
